import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  SETTINGS_RECENT_HOSTS,
  SETTINGS_MAX_RECENT_HOSTS,
} from '@/stores/localStorage';

const MAX_HOSTS = 10;

export const useRecentHostsStore = defineStore('recentHosts', () => {
  const hosts = ref<string[]>(
    getLocalStorageItem(SETTINGS_RECENT_HOSTS, []),
  );

  const maxHosts = ref<number>(
    getLocalStorageItem(SETTINGS_MAX_RECENT_HOSTS, MAX_HOSTS),
  );

  // Persist to localStorage whenever hosts or maxHosts change
  watch(hosts, (value) => setLocalStorageItem(SETTINGS_RECENT_HOSTS, value), { deep: true });
  watch(maxHosts, (value) => setLocalStorageItem(SETTINGS_MAX_RECENT_HOSTS, value));

  /**
   * Add a host to the recent hosts list.
   * If host already exists, move it to the front.
   * Remove oldest host if max capacity is reached.
   */
  function addHost(host: string): void {
    if (!host || typeof host !== 'string') return;

    // Normalize host (trim whitespace)
    const normalizedHost = host.trim();
    if (!normalizedHost) return;

    // Remove if already exists (so we can move it to front)
    hosts.value = hosts.value.filter((h) => h !== normalizedHost);

    // Add to front
    hosts.value.unshift(normalizedHost);

    // Remove oldest if exceeds max capacity
    if (hosts.value.length > maxHosts.value) {
      hosts.value = hosts.value.slice(0, maxHosts.value);
    }
  }

  /**
   * Remove a host from the recent hosts list.
   */
  function removeHost(host: string): void {
    hosts.value = hosts.value.filter((h) => h !== host);
  }

  /**
   * Clear all recent hosts.
   */
  function clearHosts(): void {
    hosts.value = [];
  }

  /**
   * Get all recent hosts (in order, most recent first).
   */
  function getHosts(): string[] {
    return [...hosts.value];
  }

  /**
   * Get the most recently used host.
   */
  function getMostRecentHost(): string | null {
    return hosts.value.length > 0 ? hosts.value[0] : null;
  }

  /**
   * Check if a host is in the recent hosts list.
   */
  function hasHost(host: string): boolean {
    return hosts.value.includes(host);
  }

  /**
   * Set the maximum number of hosts to keep.
   */
  function setMaxHosts(max: number): void {
    maxHosts.value = Math.max(1, Math.min(max, 50)); // Cap between 1 and 50
    if (hosts.value.length > maxHosts.value) {
      hosts.value = hosts.value.slice(0, maxHosts.value);
    }
  }

  return {
    hosts,
    maxHosts,
    addHost,
    removeHost,
    clearHosts,
    getHosts,
    getMostRecentHost,
    hasHost,
    setMaxHosts,
  };
});
